import { Typography, Box, Stack, Paper, DialogContentText, FormHelperText, Chip, Tooltip } from '@mui/material';
import { FormikProvider, useFormik } from 'formik';
import dayjs from 'dayjs';
import {
  dateFormatForApi,
  get24HoursTimeFormat,
  set24HoursTimeFormat,
  removeKeysFromObj,
  getPhNo10Digits,
  formatPhNo10Digit,
  checkValueNotNullUndefinedBlank,
  getOptions,
  isArrayWithLength,
  getDisplayTime,
} from '../../../utils/common-methods';
import {
  GridContainer,
  Button,
  CheckBox,
  GridItem,
  TextField,
  Select,
  Datepicker,
  Autocomplete,
  PercentTextField,
  Modal,
  FieldWarning,
  FieldLabel,
  InactiveAreaTaxModal,
} from '../../../components/shared';
import { subLocAddUpdateSchema } from '../../../schema/validationSchemas';

import useStyles from './style';
import { LoadingButton } from '@mui/lab';
import { useState, useEffect, useMemo } from 'react';
import { TextMaskCustom } from '../../../components/TextMaskCustom/TextMaskCustom';
import {
  useGetProductCategoriesQuery,
  useGetSubLocationTypesQuery,
  useGetAreaTaxLocationsQuery,
} from '../../../services/Sublocation/SublocationService';
import ConfirmationModal from '../../../components/ConfirmationModel/ConfirmationModal';
import {
  useDebounce,
  useSubLocationStatusValidation,
  useSubLocationSaveValidation,
  usePostingLocationSync,
  getPostingLocationForSubmission,
  getPostingLocationValue,
  isArcadeOrGameRoomType,
} from '../../../hooks';
import { MESSAGE } from '../../../constants/message';
import { formatTaxPercentValue } from '../../../utils/common-methods';
import { isSelectedAreaTaxLocationInactive } from '../../../utils/sublocationValidations';
import { FEED_SOURCE_OPTIONS, getCreditCashOptions } from '../../../constants/locationConstants';
import { ASSET_STATUS } from '../../../constants/assetStatus';
import { parseFeedSourceValues } from '../../../utils/common-methods';

const SubLocationUpdateDetailView = (props) => {
  const classes = useStyles();
  const [areaTaxLocationSearchText, setAreaTaxLocationSearchText] = useState('');
  const [isActiveAssets, setIsActiveAssets] = useState(false);
  const [creditCashControlValue, setCreditCashControlValue] = useState(null);

  const {
    subLocationDetails,
    onCancel,
    onSave,
    isAddNewSubLoc = false,
    loadingBtnUse = false,
    isLoading = false,
    stateData,
  } = props;

  const debouncedTaxLocationSearchText = useDebounce(areaTaxLocationSearchText, 300);

  const { currentData: locationTypesData, isSuccess: typeSuccess } = useGetSubLocationTypesQuery();

  const { currentData: productTypes } = useGetProductCategoriesQuery();
  const productTypeOptions = getOptions(productTypes);
  const productTypesInitialValues = subLocationDetails?.productCategories?.map((product) => ({
    text: product?.name,
    value: product?.id,
  }));

  const { currentData: areaTaxLocations } = useGetAreaTaxLocationsQuery({
    page: 1,
    perPage: 100,
    searchParams: `q=${debouncedTaxLocationSearchText}`,
  });

  const leaseCCFeeRate =
    subLocationDetails?.leaseCreditCardFeeRate != null ? Number(subLocationDetails.leaseCreditCardFeeRate) : undefined;

  const locationTypeOptions = locationTypesData?.map((value) => ({
    value: value?.id,
    text: value?.name,
  }));

  const countryOptions = [
    {
      text: subLocationDetails?.locationCountry,
      value: subLocationDetails?.locationCountry,
    },
  ];

  const stateOptions = [
    {
      text: subLocationDetails?.locationState,
      value: subLocationDetails?.locationState,
    },
  ];
  const cityOptions = [
    {
      text: subLocationDetails?.locationCity,
      value: subLocationDetails?.locationCity,
    },
  ];

  const taxExemptOptions = [
    { value: true, text: 'Yes' },
    { value: false, text: 'No' },
  ];

  useEffect(() => {
    const assets = Array.isArray(subLocationDetails?.assets) ? subLocationDetails.assets : [];
    const hasActiveAsset = assets.some((asset) => asset.statusId === ASSET_STATUS.ACTIVE);
    setIsActiveAssets(hasActiveAsset);
    if (subLocationDetails?.creditCashControl) {
      const validOptions = getCreditCashOptions(subLocationDetails?.calculateRent || false);
      const initialValue = validOptions.find((option) => option.value === subLocationDetails.creditCashControl);
      setCreditCashControlValue(initialValue || null);
    } else {
      setCreditCashControlValue(null);
    }
    if (typeSuccess && !isAddNewSubLoc) {
      const existingLocType = locationTypeOptions.find(
        (eachLocType) => eachLocType.value.toString() === subLocationDetails?.sublocationType?.id?.toString(),
      );
      formik.setFieldValue('type', existingLocType ? existingLocType.value : '');
    }
  }, [typeSuccess, subLocationDetails]);

  const formik = useFormik({
    initialValues: {
      name: subLocationDetails?.name?.trim() || '',
      zipCode: subLocationDetails?.code?.trim() || '',
      phone1: formatPhNo10Digit(subLocationDetails?.phone1) || '',
      phone2: formatPhNo10Digit(subLocationDetails?.phone2) || '',
      openHours: set24HoursTimeFormat(subLocationDetails?.openHours),
      closeHours: set24HoursTimeFormat(subLocationDetails?.closeHours),
      type: subLocationDetails?.sublocationType?.id || '',
      postingLocation: getPostingLocationValue(
        {
          postingLocation: subLocationDetails?.postingLocation,
          sublocationType: subLocationDetails?.sublocationType,
          name: subLocationDetails?.name,
        },
        locationTypesData,
      ),
      location: subLocationDetails?.locationName || '',
      country: subLocationDetails?.locationCountry || '',
      stateName: subLocationDetails?.locationState || '',
      cityName: subLocationDetails?.locationCity || '',
      startDate:
        checkValueNotNullUndefinedBlank(subLocationDetails?.startDate) &&
        dayjs(subLocationDetails?.startDate).toString() !== 'Invalid Date'
          ? dayjs(subLocationDetails?.startDate)
          : null,
      endDate:
        checkValueNotNullUndefinedBlank(subLocationDetails?.endDate) &&
        dayjs(subLocationDetails?.endDate).toString() !== 'Invalid Date'
          ? dayjs(subLocationDetails?.endDate)
          : null,
      uninstallDate:
        checkValueNotNullUndefinedBlank(subLocationDetails?.uninstallDate) &&
        dayjs(subLocationDetails?.uninstallDate).toString() !== 'Invalid Date'
          ? dayjs(subLocationDetails?.uninstallDate)
          : null,
      spaceIdentifier: subLocationDetails?.spaceIdentifier?.trim() || '',
      address1: subLocationDetails?.address1?.trim() || '',
      address2: subLocationDetails?.address2?.trim() || '',
      productType: isArrayWithLength(productTypesInitialValues)
        ? productTypesInitialValues[0]
        : { text: '', value: '' },
      areaTaxLocation: {
        id: subLocationDetails?.areaTaxLocations?.id,
        text: subLocationDetails?.areaTaxLocations?.areaTaxLocationName,
        value: subLocationDetails?.areaTaxLocations?.areaTaxLocationName || '',
      },
      areaTaxLocationId: subLocationDetails?.areaTaxLocations?.id || null,
      vendorCode: subLocationDetails?.vendor?.vendorCode || 'N/A',
      vendorName: subLocationDetails?.vendor?.customerName || 'N/A',
      stateTaxRate: isAddNewSubLoc
        ? stateData?.stateTaxHistories?.[0]?.stateTax || 'N/A'
        : subLocationDetails?.stateTaxHistories?.[0]?.stateTax || 'N/A',
      areaTaxRate: subLocationDetails?.areaTaxHistories?.[0]?.areaTax || 'N/A',
      miscTax: subLocationDetails?.miscTax || 0,
      ccFeePercent: subLocationDetails?.creditCardFeeRate ?? leaseCCFeeRate ?? '',
      pennyCostPercentage: subLocationDetails?.pennyCostPercentage || 0,
      locPaysTaxFlag:
        subLocationDetails?.areaTaxHistories?.[0]?.locPaysTaxFlag ?? subLocationDetails?.locPaysTaxFlag ?? false,
      calculateRent: subLocationDetails?.calculateRent || false,
      taxExemptFlag:
        subLocationDetails?.areaTaxHistories?.[0]?.taxExemptFlag ?? subLocationDetails?.taxExemptFlag ?? false,
      isCreditCardInCollection: subLocationDetails?.isCreditCardInCollection ?? true,
      feedSource: parseFeedSourceValues(subLocationDetails?.feedSource) || [],
      creditCashControl: (() => {
        if (subLocationDetails?.creditCashControl) {
          const validOptions = getCreditCashOptions(subLocationDetails?.calculateRent || false);
          return validOptions.find((option) => option.value === subLocationDetails.creditCashControl) || null;
        }
        return null;
      })(),
    },
    validationSchema: subLocAddUpdateSchema,
    onSubmit: (data) => {
      // Validate area tax location before saving
      if (!validateBeforeSave(data)) {
        return;
      }

      if (!isAddNewSubLoc) {
        data.id = subLocationDetails.id;
      }
      const productCategories = data?.productType?.id ? [data?.productType?.id] : [];
      data.openHours =
        data?.openHours && dayjs(data?.openHours).toString() !== 'Invalid Date'
          ? get24HoursTimeFormat(data?.openHours)
          : null;
      data.closeHours =
        data?.closeHours && dayjs(data?.closeHours).toString() !== 'Invalid Date'
          ? get24HoursTimeFormat(data?.closeHours)
          : null;
      data.phone1 = data?.phone1 ? getPhNo10Digits(data?.phone1) : '';
      data.phone2 = data?.phone2 ? getPhNo10Digits(data?.phone2) : '';
      data.startDate = data?.startDate ? dateFormatForApi(data?.startDate) : null;
      data.endDate = data?.endDate ? dateFormatForApi(data?.endDate) : null;
      data.uninstallDate = data?.uninstallDate ? dateFormatForApi(data?.uninstallDate) : null;
      data.code = data.zipCode.toString();
      data.type = Number(data?.type);
      data.productCategories = productCategories || [];
      data.areaTaxLocationId = data?.areaTaxLocation?.id || null;
      data.areaTaxLocation = data?.areaTaxLocation?.value || null;
      data.miscTax = data.miscTax || 0;
      data.creditCardFeeRate = data.ccFeePercent === '' ? null : data.ccFeePercent ?? null;
      data.pennyCostPercentage = data.pennyCostPercentage || 0;
      data.calculateRent = Boolean(data?.calculateRent);
      data.taxExemptFlag = data.taxExemptFlag || false;
      data.isCreditCardInCollection = formik.values.isCreditCardInCollection;
      data.feedSource = data?.feedSource?.length > 0 ? data.feedSource.map((item) => item.value) : null;
      data.creditCashControl = creditCashControlValue?.value || null;

      // For non-Arcade/GameRoom product types, postingLocation should always equal Sub Location Name
      data.postingLocation = getPostingLocationForSubmission(
        {
          productType: data.productType,
          name: data.name,
          postingLocation: data.postingLocation,
        },
        productTypes,
      );

      data = removeKeysFromObj(data, [
        'zipCode',
        'location',
        'country',
        'stateName',
        'cityName',
        'productType',
        'vendorCode',
        'vendorName',
        'stateTaxRate',
        'areaTaxRate',
      ]);
      onSave(data);
    },
  });

  const isInactiveAreaTaxLocation = useMemo(
    () => isSelectedAreaTaxLocationInactive(formik.values, subLocationDetails, areaTaxLocations),
    [formik.values.areaTaxLocation, formik.values.areaTaxLocationId, subLocationDetails, areaTaxLocations],
  );

  useEffect(() => {
    const assets = Array.isArray(subLocationDetails?.assets) ? subLocationDetails.assets : [];
    const hasActiveAsset = assets.some((asset) => asset.statusId === ASSET_STATUS.ACTIVE);
    setIsActiveAssets(hasActiveAsset);
    if (typeSuccess && !isAddNewSubLoc) {
      const existingLocType = locationTypeOptions.find(
        (eachLocType) => eachLocType.value.toString() === subLocationDetails?.sublocationType?.id?.toString(),
      );
      formik.setFieldValue('type', existingLocType ? existingLocType.value : '');
    }
  }, [typeSuccess, subLocationDetails]);

  // Auto-populate CC Fee % with the lease's default rate when sublocation doesn't have a specific value
  useEffect(() => {
    if (subLocationDetails?.creditCardFeeRate == null && leaseCCFeeRate != null && formik.values.ccFeePercent === '') {
      formik.setFieldValue('ccFeePercent', leaseCCFeeRate);
    }
  }, [leaseCCFeeRate, subLocationDetails?.creditCardFeeRate, formik.values.ccFeePercent, formik.setFieldValue]);

  // Use custom hook for status validation
  const {
    onStatusToggleClick: onStatusToggleClickHandler,
    showInactiveAreaTaxModal,
    setShowInactiveAreaTaxModal,
    showWarningModal,
    setShowWarningModal,
    showArchiveModal,
    setShowArchiveModal,
  } = useSubLocationStatusValidation(subLocationDetails, isActiveAssets);

  // Use custom hook for save validation
  const { validateBeforeSave } = useSubLocationSaveValidation(
    subLocationDetails,
    areaTaxLocations,
    setShowInactiveAreaTaxModal,
  );

  const standaloneChangeHandler = (e) => {
    const isStandalone = e.target.checked;
    setFieldValue('calculateRent', isStandalone);

    if (isStandalone) {
      setCreditCashControlValue(null);
      setFieldValue('creditCashControl', null);
    } else {
      setCreditCashControlValue(null);
      setFieldValue('creditCashControl', null);
    }
  };

  const onArchiveClickHandler = () => {
    const status = subLocationDetails?.status;
    const data = {
      id: subLocationDetails?.id,
      status: !status,
    };
    onSave(data);
  };

  const { setFieldValue, getFieldProps } = formik;

  // Auto-populate postingLocation when name or productType changes (for non-Arcade/GameRoom)
  // For non-Arcade/GameRoom product types, postingLocation should always equal Sub Location Name
  usePostingLocationSync(formik, productTypes);

  const autoCompleteChangeHandler = (name, value) => {
    setFieldValue(name, value);
    if (name === 'areaTaxLocation') {
      setAreaTaxLocationSearchText(value ? value.text : '');
      if (!value) {
        setFieldValue('areaTaxLocationId', null);
      }
    }
  };

  const onInputChangeHandler = (event, value) => {
    setAreaTaxLocationSearchText(value);
  };

  return (
    <Box component={Paper} className={classes.locationDetailsUpdateBox}>
      <Typography variant="h2">{isAddNewSubLoc ? 'New' : 'Update'} Sub Location Details</Typography>
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <GridContainer
            rowSpacing={3}
            columnSpacing={{ xs: 0, sm: 0, md: 7 }}
            className={classes.locationDetailsFieldsContainer}
            mb={4}
          >
            <GridItem xs={12} md={6} pl={0}>
              <TextField
                className={classes.formFields}
                label="Sub Location Name"
                InputProps={{
                  'data-testid': 'form-elements',
                }}
                name="name"
                onChange={formik.handleChange}
                onBlur={() => formik.setFieldTouched('name')}
                value={formik.values.name || ''}
                id="name"
                error={formik.touched.name && formik.errors.name ? true : false}
                helperText={formik.touched.name ? formik.errors.name : ''}
              />
            </GridItem>
            <GridItem xs={12} md={6} pl={0}>
              <TextField
                className={classes.formFields}
                label="Posting Location"
                InputProps={{
                  'data-testid': 'form-elements',
                }}
                name="postingLocation"
                onChange={formik.handleChange}
                onBlur={() => formik.setFieldTouched('postingLocation')}
                value={formik.values.postingLocation || ''}
                id="postingLocation"
                disabled={!isArcadeOrGameRoomType(formik.values.productType, productTypes)}
                error={formik.touched.postingLocation && formik.errors.postingLocation ? true : false}
                helperText={formik.touched.postingLocation ? formik.errors.postingLocation : ''}
              />
            </GridItem>
            <GridItem xs={12} md={6} pl={0}>
              <TextField
                className={classes.formFields}
                label="Zip code"
                InputProps={{
                  'data-testid': 'form-elements',
                }}
                name="zipCode"
                type="number"
                onChange={formik.handleChange}
                onBlur={() => formik.setFieldTouched('zipCode')}
                value={formik.values.zipCode || ''}
                id="zipCode"
                error={formik.touched.zipCode && formik.errors.zipCode ? true : false}
                helperText={formik.touched.zipCode ? formik.errors.zipCode : ''}
              />
            </GridItem>
            <GridItem xs={12} md={6} pl={0}>
              <TextField
                className={classes.formFields}
                label="Phone 1"
                InputProps={{
                  'data-testid': 'form-elements',
                  inputComponent: TextMaskCustom,
                }}
                name="phone1"
                onChange={formik.handleChange}
                onBlur={() => formik.setFieldTouched('phone1')}
                value={formik.values.phone1 || ''}
                id="phone1"
                error={formik.touched.phone1 && formik.errors.phone1 ? true : false}
                helperText={formik.touched.phone1 ? formik.errors.phone1 : ''}
              />
            </GridItem>
            <GridItem xs={12} md={6} pl={0}>
              <TextField
                className={classes.formFields}
                label="Phone 2"
                InputProps={{
                  'data-testid': 'form-elements',
                  inputComponent: TextMaskCustom,
                }}
                name="phone2"
                onChange={formik.handleChange}
                onBlur={() => formik.setFieldTouched('phone2')}
                value={formik.values.phone2 || ''}
                id="phone2"
                error={formik.touched.phone2 && formik.errors.phone2 ? true : false}
                helperText={formik.touched.phone2 ? formik.errors.phone2 : ''}
              />
            </GridItem>
            <GridItem xs={12} md={6} pl={0}>
              <Stack direction="row" spacing={2}>
                <Box sx={{ width: '50%' }}>
                  <FieldLabel
                    label="Open Hours"
                    value={getDisplayTime(formik.values.openHours)}
                    dataTestId="editSubLocation"
                  />
                </Box>
                <Box sx={{ width: '50%' }}>
                  <FieldLabel
                    label="Close Hours"
                    value={getDisplayTime(formik.values.closeHours)}
                    dataTestId="editSubLocation"
                  />
                </Box>
              </Stack>
            </GridItem>
            <GridItem xs={12} md={6} pl={0}>
              <Box className={classes.formFields}>
                {locationTypesData && typeSuccess && (
                  <Select
                    inputLabel="Sub Location Type"
                    data-testid="form-elements"
                    name="type"
                    onChange={formik.handleChange}
                    onBlur={() => formik.setFieldTouched('type')}
                    value={formik.values.type || ''}
                    id="type"
                    options={locationTypeOptions}
                    error={formik.touched.type && formik.errors.type ? true : false}
                    helperText={formik.touched.type ? formik.errors.type : ''}
                  />
                )}
              </Box>
            </GridItem>
            <GridItem xs={12} md={6} pl={0}>
              <FieldLabel label="Location" value={formik.values.location} dataTestId="form-elements" />
            </GridItem>
            <GridItem xs={12} md={6} pl={0}>
              <FieldLabel
                label="Country"
                value={countryOptions.find((option) => option.value === formik.values.country)?.text}
                dataTestId="form-elements"
              />
            </GridItem>
            <GridItem xs={12} md={6} pl={0}>
              <FieldLabel
                label="State"
                value={stateOptions.find((option) => option.value === formik.values.stateName)?.text}
                dataTestId="form-elements"
              />
            </GridItem>
            <GridItem xs={12} md={6} pl={0}>
              <FieldLabel
                label="City"
                value={cityOptions.find((option) => option.value === formik.values.cityName)?.text}
                dataTestId="form-elements"
              />
            </GridItem>
            <GridItem xs={12} md={6} pl={0}>
              <Datepicker
                className={classes.formFields}
                label="Installed Date"
                name="startDate"
                onChange={(newValue) => formik.setFieldValue('startDate', newValue)}
                onBlur={() => formik.setFieldTouched('startDate')}
                value={formik.values.startDate ? dayjs(formik.values.startDate) : null}
                id="startDate"
                inputFormat="MM/DD/YYYY"
                data-testid="form-elements"
                error={formik.touched.startDate && formik.errors.startDate ? true : false}
                helperText={formik.touched.startDate ? formik.errors.startDate : ''}
              />
              {!formik.values.startDate ? <FieldWarning field="Installed Date"></FieldWarning> : null}
            </GridItem>
            <GridItem xs={12} md={6} pl={0}>
              <Datepicker
                className={classes.formFields}
                label="Lease End Date"
                name="endDate"
                onChange={(newValue) => formik.setFieldValue('endDate', newValue)}
                onBlur={() => formik.setFieldTouched('endDate')}
                value={formik.values.endDate ? dayjs(formik.values.endDate) : null}
                id="endDate"
                inputFormat="MM/DD/YYYY"
                data-testid="form-elements"
                error={formik.touched.endDate && formik.errors.endDate ? true : false}
                helperText={formik.touched.endDate ? formik.errors.endDate : ''}
              />
              {!formik.values.endDate ? <FieldWarning field="Lease End Date"></FieldWarning> : null}
            </GridItem>
            <GridItem xs={12} md={6} pl={0}>
              <Datepicker
                className={classes.formFields}
                label="Uninstalled Date"
                name="uninstallDate"
                onChange={(newValue) => formik.setFieldValue('uninstallDate', newValue)}
                onBlur={() => formik.setFieldTouched('uninstallDate')}
                value={formik.values.uninstallDate ? dayjs(formik.values.uninstallDate) : null}
                id="uninstallDate"
                inputFormat="MM/DD/YYYY"
                data-testid="form-elements"
                error={formik.touched.uninstallDate && formik.errors.uninstallDate ? true : false}
                helperText={formik.touched.uninstallDate ? formik.errors.uninstallDate : ''}
              />
            </GridItem>
            <GridItem xs={12} md={6} pl={0}>
              <TextField
                className={classes.formFields}
                label="Space Identifier"
                InputProps={{
                  'data-testid': 'form-elements',
                }}
                name="spaceIdentifier"
                onChange={formik.handleChange}
                onBlur={() => formik.setFieldTouched('spaceIdentifier')}
                value={formik.values.spaceIdentifier || ''}
                id="spaceIdentifier"
              />
            </GridItem>
            <GridItem xs={12} md={6} pl={0}>
              <TextField
                className={classes.formFields}
                label="Address 1"
                InputProps={{
                  'data-testid': 'form-elements',
                }}
                name="address1"
                onChange={formik.handleChange}
                onBlur={() => formik.setFieldTouched('address1')}
                value={formik.values.address1 || ''}
                id="address1"
                error={formik.touched.address1 && formik.errors.address1 ? true : false}
                helperText={formik.touched.address1 ? formik.errors.address1 : ''}
                multiline
              />
            </GridItem>
            <GridItem xs={12} md={6} pl={0}>
              <TextField
                className={classes.formFields}
                label="Address 2"
                InputProps={{
                  'data-testid': 'form-elements',
                }}
                name="address2"
                onChange={formik.handleChange}
                onBlur={() => formik.setFieldTouched('address2')}
                value={formik.values.address2 || ''}
                id="address2"
                error={formik.touched.address2 && formik.errors.address2 ? true : false}
                helperText={formik.touched.address2 ? formik.errors.address2 : ''}
                multiline
              />
            </GridItem>
            <GridItem xs={12} md={6} pl={0}>
              <Autocomplete
                label="Product Type"
                data-testid="form-elements"
                value={formik.values.productType || { text: '', value: '' }}
                options={productTypeOptions || []}
                getOptionLabel={(option) => option.text || ''}
                onChange={(e, value) => {
                  formik.setFieldValue('productType', value);
                }}
                onBlur={() => formik.setFieldTouched('productType')}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                clearOnBlur={false}
                error={Boolean(
                  formik.touched.productType && (formik.errors.productType?.text || formik.errors.productType),
                )}
                helperText={
                  formik.touched.productType && (formik.errors.productType?.text || formik.errors.productType)
                }
              />
            </GridItem>
            <GridItem xs={12} md={6} pl={0}>
              <FieldLabel label="Vendor Code" value={formik.values.vendorCode} dataTestId="form-elements" />
            </GridItem>
            <GridItem xs={12} md={6} pl={0}>
              <FieldLabel label="Vendor Name" value={formik.values.vendorName} dataTestId="form-elements" />
            </GridItem>
            <GridItem xs={12} md={6} pl={0}>
              <FieldLabel
                label="State tax"
                value={
                  formik.values.stateTaxRate && String(formik.values.stateTaxRate).trim() !== ''
                    ? `${formatTaxPercentValue(formik.values.stateTaxRate)}`
                    : undefined
                }
                dataTestId="updateField"
              />
            </GridItem>
            <GridItem xs={12} md={6} pl={0}>
              <FieldLabel
                label="Area tax"
                value={
                  formik.values.areaTaxRate && String(formik.values.areaTaxRate).trim() !== ''
                    ? `${formatTaxPercentValue(formik.values.areaTaxRate)}`
                    : undefined
                }
                dataTestId="updateField"
              />
            </GridItem>
            <GridItem xs={12} md={6} pl={0}>
              <PercentTextField
                label={'Misc tax'}
                useFormik={true}
                allowDecimal
                {...getFieldProps('miscTax')}
                InputProps={{
                  'data-testid': 'updateField',
                }}
              />
              {!formik.values.miscTax ? <FieldWarning field="Misc Tax"></FieldWarning> : null}
            </GridItem>
            <GridItem xs={12} md={6} pl={0}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Tooltip
                  title={
                    subLocationDetails?.creditCardFeeRate != null
                      ? 'overridden from Sublocation edit'
                      : 'Lease Default CC Fee'
                  }
                  arrow
                >
                  <Chip
                    label={subLocationDetails?.creditCardFeeRate != null ? 'Custom CC fee' : 'Lease default CC fee'}
                    size="small"
                    color={subLocationDetails?.creditCardFeeRate != null ? 'primary' : 'default'}
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Tooltip>
              </Box>
              <PercentTextField
                label="Credit Card Fee %"
                useFormik={true}
                allowDecimal
                allowZero={true}
                {...getFieldProps('ccFeePercent')}
                InputProps={{
                  'data-testid': 'updateField',
                }}
              />
            </GridItem>
            <GridItem xs={12} md={6} pl={0}>
              <PercentTextField
                label="Penny Cost %"
                useFormik={true}
                allowDecimal
                allowZero={true}
                decimalPlaces={4}
                {...getFieldProps('pennyCostPercentage')}
                InputProps={{
                  'data-testid': 'updateField',
                }}
              />
            </GridItem>
            <GridItem xs={12} md={6} pl={0}>
              <Select
                options={taxExemptOptions}
                inputLabel="Tax Exempt"
                {...getFieldProps('taxExemptFlag')}
                data-testid="editSubLocation"
              />
            </GridItem>
            <GridItem xs={12} md={6} pl={0}>
              <Autocomplete
                label="Area Tax Location"
                value={formik.values.areaTaxLocation || null}
                options={areaTaxLocations || []}
                getOptionLabel={(option) => option.text || ''}
                onChange={(e, value) => autoCompleteChangeHandler('areaTaxLocation', value)}
                onBlur={() => formik.setFieldTouched('areaTaxLocation', true)}
                onInputChange={onInputChangeHandler}
                isOptionEqualToValue={(option, value) => option.id === value.id || option.value === value.value}
                error={isInactiveAreaTaxLocation}
                data-testid="form-elements"
              />
              {!formik.values.areaTaxLocation?.value ? (
                <FieldWarning field="Area Tax Location"></FieldWarning>
              ) : isInactiveAreaTaxLocation ? (
                <FormHelperText error>{MESSAGE.INACTIVE_AREA_TAX_SAVE_ERROR}</FormHelperText>
              ) : null}
            </GridItem>
            <GridItem xs={12} md={4} pl={1}>
              <CheckBox
                label="Location Pays Tax"
                labelPlacement="end"
                checked={formik.values.locPaysTaxFlag || false}
                name="locPaysTaxFlag"
                id="locPaysTaxFlag"
                onChange={formik.handleChange}
                data-testid="form-elements"
              />
            </GridItem>
            <GridItem xs={12} md={4} pl={0}>
              <CheckBox
                label="Standalone"
                labelPlacement="end"
                checked={formik.values.calculateRent || false}
                name="calculateRent"
                id="calculateRent"
                onChange={standaloneChangeHandler}
                data-testid="form-elements"
              />
            </GridItem>
            <GridItem xs={12} md={4}>
              <CheckBox
                label="Credit Card In Collection"
                labelPlacement="end"
                checked={formik.values.isCreditCardInCollection}
                name="isCreditCardInCollection"
                onChange={formik.handleChange}
                data-testid="form-elements"
              />
            </GridItem>
            <GridItem xs={12} md={6} pl={0}>
              <Autocomplete
                key={`creditCashControl-${formik.values.calculateRent}-${creditCashControlValue?.value || 'null'}`}
                label="Control Credit and Cash"
                data-testid="form-elements"
                value={creditCashControlValue || null}
                options={getCreditCashOptions(formik.values.calculateRent)}
                getOptionLabel={(option) => option.text || ''}
                onChange={(e, value) => {
                  setCreditCashControlValue(value);
                  formik.setFieldValue('creditCashControl', value);
                }}
                onBlur={() => formik.setFieldTouched('creditCashControl')}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                clearOnBlur={false}
                required={formik.values.calculateRent}
                error={Boolean(formik.touched.creditCashControl && formik.errors.creditCashControl)}
                helperText={formik.touched.creditCashControl && formik.errors.creditCashControl}
              />
            </GridItem>
            <GridItem xs={12} md={12} pl={0}>
              <Autocomplete
                label="Payment source"
                data-testid="form-elements"
                value={formik.values.feedSource || []}
                options={FEED_SOURCE_OPTIONS}
                getOptionLabel={(option) => option.text || ''}
                onChange={(e, value) => {
                  formik.setFieldValue('feedSource', value);
                }}
                onBlur={() => formik.setFieldTouched('feedSource')}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                clearOnBlur={false}
                multiple
                limitTags={3}
                disableCloseOnSelect
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option.text}
                      size="small"
                      {...getTagProps({ index })}
                      key={option.value}
                    />
                  ))
                }
                error={Boolean(formik.touched.feedSource && formik.errors.feedSource)}
                helperText={formik.touched.feedSource && formik.errors.feedSource}
              />
            </GridItem>
          </GridContainer>
          <GridContainer justifyContent="space-around" py={1}>
            <GridItem xs={6} className={classes.updateBtn} pb={0}>
              {loadingBtnUse ? (
                <LoadingButton
                  type="submit"
                  loading={isLoading}
                  variant="contained"
                  disabled={isInactiveAreaTaxLocation}
                >
                  {isAddNewSubLoc ? 'ADD' : 'UPDATE'}
                </LoadingButton>
              ) : (
                <Button type="submit" disabled={isInactiveAreaTaxLocation}>
                  {isAddNewSubLoc ? 'ADD' : 'UPDATE'}
                </Button>
              )}
              <Button onClick={onCancel}>CANCEL</Button>
            </GridItem>
            <GridItem xs={6} className={classes.archiveBtn} pb={0}>
              {!isAddNewSubLoc && (
                <Button
                  onClick={onStatusToggleClickHandler}
                  color={subLocationDetails?.status ? 'error' : 'success'}
                  disabled={!subLocationDetails?.status && isInactiveAreaTaxLocation}
                >
                  {subLocationDetails?.status ? 'ARCHIVE' : 'ACTIVATE'} SUB LOCATION
                </Button>
              )}
            </GridItem>
          </GridContainer>
          <Modal
            title={MESSAGE.ARCHIVE_SUBLOCATION__WARNING_TITLE}
            titleVariant="subtitle10"
            open={showWarningModal}
            onClose={() => setShowWarningModal(false)}
            actionButtons={
              <Box my={3}>
                <Button
                  size="large"
                  onClick={() => {
                    setShowWarningModal(false);
                  }}
                  variant="contained"
                >
                  {MESSAGE.OK}
                </Button>
              </Box>
            }
          >
            <Box>
              <DialogContentText variant="body1">{MESSAGE.ARCHIVE_SUBLOCATION_WARNING_DESCRIPTION}</DialogContentText>
            </Box>
          </Modal>
          <InactiveAreaTaxModal
            open={showInactiveAreaTaxModal}
            onClose={() => setShowInactiveAreaTaxModal(false)}
            title={
              formik.dirty ? MESSAGE.UNSAVED_CHANGES_WARNING_TITLE : MESSAGE.INACTIVE_AREA_TAX_LOCATION_WARNING_TITLE
            }
            description={
              formik.dirty
                ? MESSAGE.UNSAVED_CHANGES_WARNING_DESCRIPTION
                : MESSAGE.INACTIVE_AREA_TAX_LOCATION_WARNING_DESCRIPTION
            }
          />
          <ConfirmationModal
            isLoading={isLoading}
            isOpen={showArchiveModal}
            title="Confirmation"
            msg={`Are you sure you want to ${
              subLocationDetails?.status ? 'archive' : 'active'
            } this sub location? This process cannot be undone.`}
            buttons={[
              { text: subLocationDetails?.status ? 'Archive' : 'Active', value: true },
              { text: 'Cancel', value: false },
            ]}
            onClick={(value) => {
              if (value) onArchiveClickHandler();
              else setShowArchiveModal(false);
            }}
          />
        </form>
      </FormikProvider>
    </Box>
  );
};

export default SubLocationUpdateDetailView;
